<template>
  <section class="indicators-page" :dir="pageDirection">
    <div class="page-header">
      <h1>{{ t("indicatorsPage.title") }}</h1>
      <p>{{ t("indicatorsPage.description") }}</p>
    </div>

    <div class="summary-row">
      <div class="counter">{{ t("indicatorsPage.counter") }}</div>
    </div>

    <div v-for="axis in axes" :key="axis.code" class="axis-card">
      <div class="axis-title">
        <span class="dot" :style="{ background: axis.color }"></span>
        <strong>{{ axis.title }}</strong>
        <small>{{ axis.meta }}</small>
      </div>

      <p class="desc">{{ axis.desc }}</p>

      <div v-if="axis.notes.length" class="info-box">
        <p v-for="note in axis.notes" :key="note">{{ note }}</p>
      </div>

      <div class="table-wrap">
        <table>
          <thead>
            <tr>
              <th>{{ t("indicatorsPage.table.code") }}</th>
              <th>{{ t("indicatorsPage.table.indicator") }}</th>
              <th>{{ t("indicatorsPage.table.formula") }}</th>
              <th>{{ t("indicatorsPage.table.unit") }}</th>
              <th>{{ t("indicatorsPage.table.standard") }}</th>
            </tr>
          </thead>

          <tbody>
            <tr v-for="item in axis.indicators" :key="item.code">
              <td class="code" :data-label="t('indicatorsPage.table.code')">
                {{ item.code }}
              </td>

              <td
                class="name"
                :data-label="t('indicatorsPage.table.indicator')"
              >
                {{ item.name }}
              </td>

              <td :data-label="t('indicatorsPage.table.formula')">
                {{ item.formula }}
              </td>

              <td class="unit" :data-label="t('indicatorsPage.table.unit')">
                {{ item.unit }}
              </td>

              <td :data-label="t('indicatorsPage.table.standard')">
                <span class="badge">{{ item.standard }}</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <div class="axis-card">
      <div class="axis-title">
        <span class="dot equity"></span>
        <strong>{{ t("indicatorsPage.equity.title") }}</strong>
        <small>{{ t("indicatorsPage.equity.meta") }}</small>
      </div>

      <p class="desc">{{ t("indicatorsPage.equity.description") }}</p>

      <div class="table-wrap">
        <table>
          <thead>
            <tr>
              <th>{{ t("indicatorsPage.hardTriggers.trigger") }}</th>
              <th>{{ t("indicatorsPage.table.indicator") }}</th>
              <th>{{ t("indicatorsPage.hardTriggers.rule") }}</th>
            </tr>
          </thead>

          <tbody>
            <tr v-for="item in hardTriggers" :key="item.code">
              <td
                class="code"
                :data-label="t('indicatorsPage.hardTriggers.trigger')"
              >
                {{ item.code }}
              </td>

              <td
                class="name"
                :data-label="t('indicatorsPage.table.indicator')"
              >
                {{ item.indicator }}
              </td>

              <td :data-label="t('indicatorsPage.hardTriggers.rule')">
                {{ item.rule }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </section>
</template>

<script setup>
import { computed } from "vue";
import { useI18n } from "vue-i18n";

const { t, locale } = useI18n();
const pageDirection = computed(() => (locale.value === "ar" ? "rtl" : "ltr"));

const axesData = [
  {
    code: "INF",
    title: "محور البنية التحتية والأصول",
    meta: "(16 مؤشراً — وزن المحور 25% في درجة الأولوية المركبة)",
    desc: "الحالة المادية تحدد الجدوى التشغيلية للمرفق",
    color: "#143a63",
    notes: [
      "فئات احتياجات إعادة التأهيل: إنشائي (مدني) 78% · غير إنشائي 67% · الكهرباء 56% · المياه والصرف 44% · المعدات الطبية 33% · النوع الاجتماعي 22%",
      "مصادر الطاقة والمياه: شبكة كهرباء عامة 7/9 · مولّد كهربائي 8/9 · طاقة شمسية 3/9 · شبكة مياه 6/9 · صهاريج نقل 4/9 · بئر/حفر 2/9",
    ],
    indicators: [
      [
        "INF-01",
        "مؤشر الحالة الإنشائية للمرفق (FSCI)",
        "Σ مؤشر أضرار المبنى (BDI) ÷ عدد المباني؛ BDI = Σ(wᵢ×dᵢ) ÷ Σwᵢ",
        "مقياس 1–5",
        "الدرجة ≥3 = غير صالح؛ أي عنصر إنشائي أساسي بدرجة 5 ⇒ المبنى غير آمن فوراً",
      ],
      [
        "INF-02",
        "معدل الأضرار الإنشائية الشديدة",
        "(المرافق بأضرار إنشائية كبيرة/مهددة أو انهيار جزئي/كلي ÷ الإجمالي) × 100",
        "%",
        "عتبة التدخل الفوري: ≥25%",
      ],
      [
        "INF-03",
        "معدل الامتثال للتخزين الآمن",
        "(المرافق بغرف تخزين آمنة ÷ الإجمالي) × 100",
        "%",
        "≥80%",
      ],
      [
        "INF-04",
        "مؤشر جاهزية السلامة من الحرائق",
        "(المرافق بطفايات حريق متوفرة وكلها صالحة ÷ الإجمالي) × 100",
        "%",
        "≥80%",
      ],
      [
        "INF-05",
        "معدل أداء المعدات الطبية",
        "المعدات العاملة ÷ المطلوبة × 100",
        "%",
        "ممتاز ≥80؛ جيد 60–79؛ مقبول 40–59؛ ضعيف <40",
      ],
      [
        "INF-06",
        "معدل أداء غرف العمليات/الطوارئ",
        "الغرف العاملة ÷ المطلوبة × 100",
        "%",
        "≥80%",
      ],
      [
        "INF-07",
        "معدل توفر الكهرباء",
        "(ساعات الكهرباء/اليوم ÷ 24) × 100",
        "%",
        "موثوق ≥18 ساعة (75%)؛ جزئي 8–18؛ غير موثوق <8",
      ],
      [
        "INF-08",
        "مؤشر موثوقية الطاقة",
        "(عدد مصادر الطاقة المتاحة ÷ 3) × 100",
        "0–100",
        "≥67",
      ],
      [
        "INF-09",
        "مؤشر السلامة الكهربائية",
        "سلامة الأسلاك + قواطع الدوائر + غياب الأسلاك المكشوفة + غياب الدوائر المحمّلة (÷4 × 100)",
        "0–100",
        "≥80",
      ],
      [
        "INF-10",
        "معدل الوصول إلى مياه آمنة",
        "(أيام المياه الآمنة/الشهر ÷ 30) × 100",
        "%",
        "≥90%",
      ],
      [
        "INF-11",
        "درجة الصرف الصحي",
        "مراحيض عاملة (40) + محطات غسل اليدين (30) + التخلص من النفايات الطبية (30)",
        "0–100",
        "≥70",
      ],
      [
        "INF-12",
        "مؤشر وصول ذوي الإعاقة",
        "منحدرات مناسبة (30) + ممرات واسعة (20) + مراحيض ميسّرة (25) + لافتات واضحة (15) + بيئة خالية من العوائق (10)",
        "0–100",
        "ممتاز ≥80؛ جيد 60–79؛ محدود 40–59؛ غير ميسّر <40",
      ],
      [
        "INF-13",
        "مؤشر سلامة المرأة والخصوصية",
        "نعم=1، جزئياً=0.5، لا=0؛ المؤشر = المجموع ÷ المعايير المنطبقة × 100",
        "%",
        "≥80%؛ مُشغّل صلب HT-1 إذا <50",
      ],
      [
        "INF-14",
        "تقييم السلامة المحيطية والإضاءة الليلية",
        "إضاءة ليلية كافية + مسارات خارجية آمنة + سور محيطي عامل (÷3 × 100)",
        "%",
        "≥80",
      ],
      [
        "INF-15",
        "تقدير تكلفة إعادة التأهيل",
        "إجمالي تكلفة إعادة التأهيل المقدّرة للمرفق",
        "ريال يمني",
        "قيمة مطلقة — بلا معيار لوني",
      ],
      [
        "INF-16",
        "تقدير التكلفة المتكررة",
        "إجمالي تكاليف الصيانة السنوية المقدّرة",
        "ريال يمني",
        "قيمة مطلقة — بلا معيار لوني",
      ],
    ],
  },
  {
    code: "SRV",
    title: "محور تقديم الخدمات",
    meta: "(8 مؤشرات — وزن المحور 30% في درجة الأولوية المركبة)",
    desc: "يعكس مباشرة الأثر على وصول السكان إلى الخدمة الصحية",
    color: "#0f766e",
    notes: [
      "رعاية ما قبل الولادة (ANC): اضطراب موثّق: غياب القابلات، عدم توفر كادر نسائي، وتضرر أجنحة الأمومة.",
      "الإدارة السريرية للعنف القائم على النوع الاجتماعي: نقص الكوادر المدربة، غياب الخصوصية، وانعدام حقائب الوقاية بعد التعرض (PEP).",
    ],
    indicators: [
      [
        "SRV-01",
        "معدل التعطل الكلي للخدمات",
        "الخدمات المتوقفة كلياً ÷ إجمالي الخدمات القياسية × 100",
        "%",
        "عتبة التنبيه: ≥25%",
      ],
      [
        "SRV-02",
        "معدل انخفاض الاستخدام",
        "(زيارات ما قبل الأزمة − الزيارات الحالية) ÷ زيارات ما قبل الأزمة × 100",
        "%",
        "عتبة التنبيه: ≥20%",
      ],
      [
        "SRV-03",
        "مؤشر صحة الأم والمولود الجديد (MNH)",
        "رعاية ما قبل الولادة (20) + ولادة آمنة (25) + ما بعد الولادة (15) + تنظيم الأسرة (15) + رعاية المواليد (15) + التحصين (10)",
        "0–100",
        "شامل ≥80؛ جيد 60–79؛ محدود 40–59؛ حرج <40",
      ],
      [
        "SRV-04",
        "معدل توفر الأدوية الأساسية",
        "فئات الأدوية المتاحة ÷ قائمة الأدوية الأساسية × 100",
        "%",
        "الهدف الوطني: ≥70%",
      ],
      [
        "SRV-05",
        "مؤشر شمولية حزمة الخدمات (SPCI)",
        "(الخدمات العاملة كلياً + 0.5 × العاملة جزئياً) ÷ إجمالي الخدمات القياسية × 100",
        "0–100",
        "كامل ≥80؛ مقبول 60–79؛ حرج <60",
      ],
      [
        "SRV-06",
        "مؤشر الجاهزية للطوارئ",
        "قسم طوارئ عامل + كادر 24/7 + معدات + إسعاف + كهرباء + مياه",
        "0–100",
        "جاهز كلياً ≥80؛ جزئي 60–79؛ غير جاهز <60",
      ],
      [
        "SRV-07",
        "مؤشر القدرة التشغيلية (OCI)",
        "إجمالي نقاط التشغيل ÷ الحد الأقصى × 100",
        "%",
        "≥80%",
      ],
      [
        "SRV-08",
        "معدل اضطراب الإمداد",
        "تكرار نفاد الإمدادات لكل ربع سنة",
        "عدد/ربع",
        "<3 لكل ربع",
      ],
    ],
  },
  {
    code: "HRH",
    title: "محور القوى العاملة الصحية",
    meta: "(9 مؤشرات — وزن المحور 20% في درجة الأولوية المركبة)",
    desc: "وجود الكادر شرط مسبق لكل الخدمات",
    color: "#7c3aed",
    notes: [
      "إجمالي الوظائف المعتمدة: 1,041 · الوظائف المشغولة: 1,041 · متوسط التوفر اليومي: ~85% · تغطية 24/7: جزئي · التدريب (آخر 12 شهراً): متفاوت",
    ],
    indicators: [
      [
        "HRH-01",
        "معدل تغطية الموظفين",
        "الكادر الحالي ÷ الكادر المطلوب (المعايير الوطنية) × 100",
        "%",
        "عتبة التنبيه: <60%",
      ],
      [
        "HRH-02",
        "كثافة العاملين الصحيين",
        "عدد العاملين الصحيين ÷ سكان نطاق التغطية × 10,000",
        "لكل 10,000",
        "الهدف الوطني: ≥10 أطباء لكل 10,000",
      ],
      [
        "HRH-03",
        "مؤشر كفاية القوى العاملة الصحية (HWAI)",
        "التوظيف (40%) + الكثافة (25%) + تغطية الأقسام (20%) + التدريب (15%)",
        "0–100",
        "مرتفع ≥80؛ جيد 60–79؛ نقص متوسط 40–59؛ نقص حاد <40",
      ],
      [
        "HRH-04",
        "نسبة الكوادر الإناث",
        "الكادر النسائي ÷ إجمالي الكادر × 100",
        "%",
        "≥30% بالمرافق المختلطة؛ ≥50% في خدمات الأمومة",
      ],
      [
        "HRH-05",
        "معدل تغطية التدريب",
        "المدرَّبون خلال 12 شهراً ÷ إجمالي الكادر × 100",
        "%",
        "الهدف: ≥40%",
      ],
      [
        "HRH-06",
        "مؤشر استقرار الكادر",
        "الكادر الدائم ÷ إجمالي الكادر × 100",
        "%",
        "مستقر ≥70؛ جزئي 50–69؛ غير مستقر <50",
      ],
      [
        "HRH-07",
        "مؤشر دمج النوع الاجتماعي",
        "مساحات منفصلة للنساء + خصوصية + كادر نسائي + إضاءة وأمن + مراحيض منفصلة",
        "%",
        "ممتاز ≥80؛ جيد 60–79؛ مقبول 40–59؛ ضعيف <40",
      ],
      [
        "HRH-08",
        "الفجوة التشغيلية حسب الأقسام",
        "الأقسام عند الحد الأدنى للتوظيف ÷ إجمالي الأقسام × 100",
        "%",
        "≥80%",
      ],
      [
        "HRH-09",
        "معدل المتطوعين/غير المنتظمين",
        "المتطوعون ÷ إجمالي الكادر × 100",
        "%",
        "<20%",
      ],
    ],
  },
  {
    code: "FIN",
    title: "محور التمويل الصحي",
    meta: "(5 مؤشرات — وزن المحور 15% في درجة الأولوية المركبة)",
    desc: "الهشاشة المالية تقود اضطراب الخدمات",
    color: "#ca8a04",
    notes: [
      "الفجوة التشغيلية الشهرية: 27 مليون ريال (≈ $17,881) · أعلى فجوة: مستشفى الصداقة · تأخر صرف المرتبات: موثّق · رسوم المرضى: تفرضها معظم المرافق · إعفاءات للفئات الهشة: نعم",
    ],
    indicators: [
      [
        "FIN-01",
        "معدل التغطية المالية",
        "الموازنة المتاحة ÷ الموازنة التشغيلية المطلوبة × 100",
        "%",
        "≥80%؛ عتبة تنبيه الدليل <50%",
      ],
      [
        "FIN-02",
        "معدل فجوة التمويل",
        "فجوة التمويل ÷ الموازنة التشغيلية المطلوبة × 100",
        "%",
        "عتبة التنبيه: ≥50%",
      ],
      [
        "FIN-03",
        "مؤشر تنوّع مصادر التمويل",
        "مصدر واحد = 25؛ مصدران = 50؛ ثلاثة = 75؛ أربعة فأكثر = 100",
        "0–100",
        "الهدف: ≥75",
      ],
      [
        "FIN-04",
        "معدل الاعتماد على التمويل الخارجي",
        "التمويل الخارجي ÷ إجمالي التمويل × 100",
        "%",
        "عتبة التنبيه: ≥70%",
      ],
      [
        "FIN-05",
        "مؤشر المرونة المالية للمرفق الصحي (HFFRI)",
        "التغطية المالية (40%) + الفجوة (25%) + التنوّع (20%) + الاستدامة (15%)",
        "0–100",
        "مرتفع ≥80؛ جيد 60–79؛ محدود 40–59؛ ضعيف <40",
      ],
    ],
  },
  {
    code: "GOV",
    title: "محور الحوكمة والمعلومات",
    meta: "(5 مؤشرات — وزن المحور 10% في درجة الأولوية المركبة)",
    desc: "جودة الحوكمة تحدد فاعلية الإدارة",
    color: "#be185d",
    notes: [
      "خطط الطوارئ: تغطية جزئية — التفعيل أثناء الأزمات غير منتظم · منصات التنسيق: مشاركة فعّالة في الكتلة الصحية واجتماعات الوزارة · نظام DHIS2: استخدام جزئي — جودة البيانات تحتاج تحسيناً",
    ],
    indicators: [
      [
        "GOV-01",
        "مؤشر الحوكمة المؤسسية",
        "مدير مؤهل + هيكل تنظيمي عامل + اجتماعات إدارية منتظمة + خطط موثّقة + آليات مساءلة",
        "0–100",
        "الهدف: ≥80",
      ],
      [
        "GOV-02",
        "مؤشر جاهزية نظام المعلومات الصحية (HMIS)",
        "(مجموع النقاط ÷ الحد الأقصى) × 100 عبر 10 معايير",
        "0–100",
        "الهدف: ≥80",
      ],
      [
        "GOV-03",
        "معدل إكمال التقارير الصحية",
        "(التقارير المسلّمة في موعدها ÷ التقارير المطلوبة) × 100",
        "%",
        "≥90%",
      ],
      [
        "GOV-04",
        "مؤشر المساءلة المجتمعية",
        "(درجة آليات الشكاوى والمساءلة ÷ الحد الأقصى) × 100",
        "0–100",
        "الهدف: ≥70",
      ],
      [
        "GOV-05",
        "مؤشر التأهب للطوارئ",
        "(عناصر التأهب المتوفرة ÷ إجمالي العناصر) × 100: خطة + تدريب + بروتوكولات + مخزون + تنسيق",
        "0–100",
        "الهدف: ≥80",
      ],
    ],
  },
];

const hardTriggersData = [
  {
    code: "HT-1",
    indicator: "سلامة المرأة والخصوصية (INF-13)",
    rule: "أي مرفق دون 50 يُرفّع تلقائياً إلى حد أدنى P2 — عالية؛ ودون 30 مع خدمات أمومة نشطة يُرفّع إلى P1 — حرجة.",
  },
  {
    code: "HT-2",
    indicator: "دمج النوع الاجتماعي (HRH-07)",
    rule: "خرق العتبة الحرجة لمؤشر دمج النوع الاجتماعي يستوجب ترفيع درجة الأولوية وفق قواعد الدليل.",
  },
  {
    code: "HT-3",
    indicator: "وصول ذوي الإعاقة (INF-12)",
    rule: "أي مرفق دون 30 في مؤشر وصول ذوي الإعاقة يخضع للترفيع.",
  },
  {
    code: "HT-S",
    indicator: "السلامة الإنشائية (INF-01)",
    rule: "أي عنصر إنشائي أساسي بدرجة BDI = 5 يُصنّف المبنى غير آمن للاستخدام فوراً.",
  },
];

const axes = computed(() =>
  axesData.map((axis) => ({
    ...axis,
    title: t(`indicatorsPage.axes.${axis.code}.title`),
    meta: t(`indicatorsPage.axes.${axis.code}.meta`),
    desc: t(`indicatorsPage.axes.${axis.code}.desc`),
    notes: axis.notes.map((_, index) =>
      t(`indicatorsPage.axes.${axis.code}.notes.${index}`)
    ),
    indicators: axis.indicators.map((item) => ({
      code: item[0],
      name: t(`indicatorsPage.indicators.${item[0]}.name`),
      formula: t(`indicatorsPage.indicators.${item[0]}.formula`),
      unit: t(`indicatorsPage.indicators.${item[0]}.unit`),
      standard: t(`indicatorsPage.indicators.${item[0]}.standard`),
    })),
  }))
);

const hardTriggers = computed(() =>
  hardTriggersData.map((item) => ({
    ...item,
    indicator: t(`indicatorsPage.hardTriggers.items.${item.code}.indicator`),
    rule: t(`indicatorsPage.hardTriggers.items.${item.code}.rule`),
  }))
);
</script>

<style scoped>
.indicators-page {
  min-height: 100vh;
  background: #f4f6f9;
  padding: 24px;
  color: #071126;
  font-family: "Cairo";
}

.page-header {
  margin-bottom: 22px;
}

.page-header h1 {
  font-size: 26px;
  font-weight: 900;
  margin-bottom: 8px;
}

.page-header p {
  color: #52657d;
  font-size: 14px;
  line-height: 1.8;
}

.summary-row {
  display: flex;
  justify-content: flex-start;
  margin-bottom: 18px;
}

.counter {
  color: #64748b;
  font-size: 14px;
  font-weight: 800;
}

.axis-card {
  background: #fff;
  border: 1px solid #dbe3ee;
  border-radius: 12px;
  padding: 22px;
  margin-bottom: 22px;
  box-shadow: 0 1px 4px rgba(15, 35, 64, 0.05);
}

.axis-title {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  border-bottom: 1px solid #dbe3ee;
  padding-bottom: 14px;
  margin-bottom: 16px;
}

.dot {
  width: 12px;
  height: 12px;
  border-radius: 4px;
}

.equity {
  background: #db2777;
}

.axis-title strong {
  font-size: 18px;
  font-weight: 900;
}

.axis-title small {
  font-size: 12px;
  color: #64748b;
}

.desc {
  color: #64748b;
  font-size: 14px;
  margin-bottom: 14px;
}

.info-box {
  background: #f8fafc;
  border: 1px solid #dbe3ee;
  border-radius: 12px;
  padding: 16px 18px;
  margin-bottom: 12px;
  line-height: 2;
  font-size: 13px;
  color: #334155;
}

.table-wrap {
  width: 100%;
  overflow: hidden;
  border: 1px solid #dbe3ee;
  border-radius: 10px;
}

table {
  width: 100%;
  min-width: 0;
  table-layout: fixed;
  border-collapse: collapse;
}

thead {
  background: #143a63;
}

th {
  color: #fff;
  font-size: 13px;
  font-weight: 900;
  padding: 15px 14px;
  text-align: right;
  white-space: normal;
  overflow-wrap: anywhere;
}

td {
  padding: 14px;
  border-bottom: 1px solid #dbe3ee;
  font-size: 13px;
  line-height: 1.8;
  vertical-align: top;
}

th:nth-child(1),
td:nth-child(1) {
  width: 9%;
}

th:nth-child(2),
td:nth-child(2) {
  width: 23%;
}

th:nth-child(3),
td:nth-child(3) {
  width: 38%;
}

th:nth-child(4),
td:nth-child(4) {
  width: 10%;
}

th:nth-child(5),
td:nth-child(5) {
  width: 20%;
}

tbody tr:hover {
  background: #f8fafc;
}

.code {
  font-weight: 900;
  direction: ltr;
  text-align: center;
  white-space: nowrap;
}

.name {
  font-weight: 900;
  min-width: 220px;
}

.unit {
  text-align: center;
  white-space: nowrap;
}

.badge {
  display: inline-flex;
  align-items: center;
  background: #f8fafc;
  border-radius: 999px;
  padding: 7px 13px;
  font-size: 12px;
  color: #334155;
  font-weight: 700;
}

@media (max-width: 900px) {
  .indicators-page {
    padding: 14px 8px 30px;
    overflow-x: hidden;
  }

  .page-header {
    margin-bottom: 16px;
  }

  .page-header h1 {
    font-size: 19px;
    line-height: 1.6;
  }

  .page-header p {
    font-size: 12px;
    line-height: 1.8;
  }

  .summary-row {
    margin-bottom: 12px;
  }

  .counter {
    font-size: 12px;
  }

  .axis-card {
    padding: 12px;
    margin-bottom: 14px;
    border-radius: 16px;
  }

  .axis-title {
    gap: 6px;
    padding-bottom: 10px;
    margin-bottom: 12px;
  }

  .axis-title strong {
    font-size: 15px;
    line-height: 1.6;
  }

  .axis-title small {
    width: 100%;
    font-size: 11px;
    line-height: 1.6;
  }

  .desc,
  .info-box {
    font-size: 12px;
    line-height: 1.8;
  }

  .info-box {
    padding: 12px;
  }

  /* ================= Mobile cards ================= */

  .table-wrap {
    overflow: visible;
    border: 0;
    border-radius: 0;
  }

  table,
  thead,
  tbody,
  tr,
  th,
  td {
    display: block;
    width: 100%;
  }

  table {
    min-width: 0;
    table-layout: auto;
  }

  thead {
    display: none;
  }

  tbody {
    display: grid;
    gap: 12px;
  }

  tbody tr {
    position: relative;
    display: block;
    width: 100%;
    min-width: 0;
    padding: 14px;
    border: 1px solid #dbe3ee;
    border-inline-start: 5px solid #143a63;
    border-radius: 16px;
    background: #fff;
    box-shadow: 0 6px 18px rgba(15, 35, 64, 0.06);
  }

  tbody tr:hover {
    background: #fff;
  }

  tbody td,
  tbody td:nth-child(1),
  tbody td:nth-child(2),
  tbody td:nth-child(3),
  tbody td:nth-child(4),
  tbody td:nth-child(5) {
    display: flex;
    width: 100%;
    min-width: 0;
    max-width: none;
    align-items: flex-start;
    justify-content: space-between;
    gap: 14px;
    padding: 10px 0;
    border-bottom: 1px dashed #e2e8f0;
    font-size: 12px;
    line-height: 1.8;
    text-align: end;
    white-space: normal;
    word-break: normal;
    overflow-wrap: break-word;
  }

  tbody td:last-child {
    border-bottom: 0;
  }

  tbody td::before {
    content: attr(data-label);
    display: block;
    flex: 0 0 105px;
    width: 105px;
    color: #64748b;
    font-size: 11px;
    font-weight: 800;
    line-height: 1.6;
    text-align: start;
    white-space: normal;
  }

  /*
   * اسم المؤشر يظهر في أعلى البطاقة بعرض كامل
   */
  tbody td.name {
    display: block;
    width: 100%;
    min-width: 0;
    padding: 4px 0 14px;
    color: #071126;
    font-size: 15px;
    font-weight: 900;
    line-height: 1.8;
    text-align: start;
    white-space: normal;
    word-break: normal;
    overflow-wrap: break-word;
  }

  tbody td.name::before {
    display: none;
    content: none;
  }

  /*
   * كود المؤشر يظهر كشارة أعلى البطاقة
   */
  tbody td.code {
    display: inline-flex;
    width: auto;
    min-width: 76px;
    max-width: 100%;
    padding: 7px 12px;
    margin-bottom: 8px;
    align-items: center;
    justify-content: center;
    border: 1px solid #bfdbfe;
    border-bottom: 1px solid #bfdbfe;
    border-radius: 999px;
    background: #eff6ff;
    color: #143a63;
    font-size: 12px;
    font-weight: 900;
    direction: ltr;
    text-align: center;
    white-space: nowrap;
  }

  tbody td.code::before {
    display: none;
    content: none;
  }

  tbody td.unit {
    text-align: end;
  }

  .badge {
    max-width: calc(100% - 119px);
    padding: 6px 10px;
    border-radius: 10px;
    font-size: 10px;
    line-height: 1.7;
    white-space: normal;
    text-align: end;
  }
}

@media (max-width: 480px) {
  .indicators-page {
    padding: 10px 6px 24px;
  }

  .axis-card {
    padding: 10px;
  }

  tbody tr {
    padding: 12px;
  }

  tbody td::before {
    flex-basis: 88px;
    width: 88px;
    font-size: 10px;
  }

  .badge {
    max-width: calc(100% - 102px);
  }
}

.indicators-page[dir="ltr"] {
  text-align: left;
}

.indicators-page[dir="ltr"] th,
.indicators-page[dir="ltr"] td {
  text-align: left;
}

.indicators-page[dir="ltr"] .code,
.indicators-page[dir="ltr"] .unit {
  text-align: center;
}
</style>
